import React, { useEffect, useState } from 'react';
import { categoriesApi } from '../services/api';
import { Button } from './ui/button';

interface CategoryExclusionSelectProps {
    value: number[];
    onChange: (excludedIds: number[]) => void;
}

export function useAllCategoriesExcluded(excludedIds: number[]): boolean {
    const [total, setTotal] = useState(0);
    useEffect(() => {
        if (cachedCategories) {
            setTotal(cachedCategories.filter(c => c.id >= 102).length);
            return;
        }
        categoriesApi.getAll().then(data => {
            cachedCategories = data;
            setTotal(data.filter(c => c.id >= 102).length);
        }).catch(() => {});
    }, []);
    return total > 0 && excludedIds.length >= total;
}

interface CategoryEntry {
    id: number;
    title: string;
}

const SENSITIVE_RANGE_START = 128;

let cachedCategories: CategoryEntry[] | null = null;

const CategoryExclusionSelect: React.FC<CategoryExclusionSelectProps> = ({ value, onChange }) => {
    const [expanded, setExpanded] = useState(false);
    const [categories, setCategories] = useState<CategoryEntry[]>(cachedCategories || []);
    const [loading, setLoading] = useState(!cachedCategories);

    useEffect(() => {
        if (cachedCategories) return;
        categoriesApi.getAll()
            .then(data => {
                cachedCategories = data;
                setCategories(data);
            })
            .catch(err => console.error('Failed to load categories:', err))
            .finally(() => setLoading(false));
    }, []);

    const visibleEntries = categories.filter(c => c.id >= 102);

    const toggle = (id: number) => {
        onChange(
            value.includes(id)
                ? value.filter(v => v !== id)
                : [...value, id]
        );
    };

    const includeAll = () => onChange([]);
    const excludeAll = () => onChange(visibleEntries.map(c => c.id));
    const excludeSensitive = () => onChange(
        visibleEntries.filter(c => c.id >= SENSITIVE_RANGE_START).map(c => c.id)
    );

    const excludedCount = value.length;
    const allExcluded = visibleEntries.length > 0 && excludedCount >= visibleEntries.length;

    return (
        <div className="border border-line rounded-lg">
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
                <div>
                    <span className="text-sm font-medium text-ink">
                        Excluded URL Categories
                    </span>
                    <p className={`text-xs mt-0.5 ${allExcluded ? 'text-risk font-medium' : 'text-ink-muted'}`}>
                        {allExcluded
                            ? 'All categories excluded — will match zero URLs'
                            : excludedCount > 0
                                ? `${excludedCount} categor${excludedCount === 1 ? 'y' : 'ies'} excluded from URL fetching`
                                : 'No categories excluded'}
                    </p>
                </div>
                <svg
                    className={`w-4 h-4 text-ink-muted transform transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {expanded && (
                <div className="px-4 pb-4 border-t border-line pt-3 space-y-3">
                    {loading ? (
                        <p className="text-xs text-ink-muted">Loading categories...</p>
                    ) : (
                        <>
                            <div className="flex gap-2 flex-wrap">
                                <Button type="button" variant="outline-safe" size="xs" onClick={includeAll}>
                                    Include All
                                </Button>
                                <Button type="button" variant="outline-risk" size="xs" onClick={excludeAll}>
                                    Exclude All
                                </Button>
                                <Button type="button" variant="outline-warn" size="xs" onClick={excludeSensitive}>
                                    Exclude Sensitive
                                </Button>
                            </div>
                            {allExcluded && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-risk-bg border border-risk rounded-md">
                                    <svg className="w-4 h-4 text-risk flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <p className="text-xs text-risk">All categories are excluded. This will match zero URLs.</p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {visibleEntries.map(({ id, title }) => {
                                    const excluded = value.includes(id);
                                    return (
                                        <label
                                            key={id}
                                            className="flex items-center gap-2 cursor-pointer py-0.5"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={excluded}
                                                onChange={() => toggle(id)}
                                                className="rounded border-line text-accent focus:ring-accent h-3.5 w-3.5"
                                            />
                                            <span className={`text-xs ${excluded ? 'text-ink-muted line-through' : 'text-ink'}`}>
                                                {title}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CategoryExclusionSelect;
